import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Link, Pagination, Typography } from '@mui/material';
import { Storefront } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ProductsService } from '../../../api/services/ProductsService';
import type { ProductStockUnitDto } from '../../../api/models/ProductStockUnitDto';
import { usePermission } from '../../../hooks/usePermission';
import { INVENTORY_PERMISSIONS } from '../../../constants/permissions.constants';
import { INV_AVAILABILITY_BLOCKED_LABEL } from '../../../constants/inventory-status-labels';
import { StatusChip } from '../../ui/StatusChip';
import { TABLE_ACTION_ICON_COLOR } from '../../../styles/theme/tableActionIcons';
import { CATALOG_STOCK_UNITS_PAGE_SIZE } from './catalogStock.constants';
import {
  InfoCard,
  CardHeader,
  CardTitle,
  CardContent,
  NoTagsText,
} from './ProductDetailModal.styles';

function availabilityLabel(status: string): string {
  if (status === 'RESERVED') {
    return INV_AVAILABILITY_BLOCKED_LABEL;
  }
  return 'Disponible';
}

function availabilityChipStatus(status: string): 'success' | 'warning' {
  return status === 'RESERVED' ? 'warning' : 'success';
}

function pieceCountLabel(total: number): string {
  return total === 1 ? '1 pieza' : `${total} piezas`;
}

interface ProductCatalogStockSectionProps {
  productId: number | null;
  open: boolean;
  accentColor: string;
  cardHeaderSx: (accentColor: string) => object;
  cardTitleSx: (accentColor: string) => object;
}

const ProductCatalogStockSection: React.FC<ProductCatalogStockSectionProps> = ({
  productId,
  open,
  accentColor,
  cardHeaderSx,
  cardTitleSx,
}) => {
  const navigate = useNavigate();
  const canViewInventoryGeneral = usePermission(INVENTORY_PERMISSIONS.GENERAL.VIEW);
  const [page, setPage] = useState(1);
  const [units, setUnits] = useState<ProductStockUnitDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setPage(1);
  }, [open, productId]);

  useEffect(() => {
    if (!open || productId == null) {
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await ProductsService.productsControllerFindCatalogStockUnits(
          productId,
          page,
          CATALOG_STOCK_UNITS_PAGE_SIZE,
        );
        if (cancelled) {
          return;
        }
        setUnits(response?.data ?? []);
        setTotal(response?.meta?.total ?? 0);
      } catch (loadError: unknown) {
        // Distinguish a failed load from a genuinely empty branch stock.
        console.error(loadError);
        if (!cancelled) {
          setUnits([]);
          setTotal(0);
          setError('No se pudieron cargar las piezas en sucursal.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [open, productId, page]);

  const handleOpenInGeneralInventory = useCallback(
    (productCodeUnique: string) => {
      navigate(`/dashboard/inventory/general?search=${encodeURIComponent(productCodeUnique)}`);
    },
    [navigate],
  );

  const totalPages = Math.ceil(total / CATALOG_STOCK_UNITS_PAGE_SIZE) || 1;
  const showPagination = total > CATALOG_STOCK_UNITS_PAGE_SIZE;

  return (
    <InfoCard>
      <CardHeader
        sx={{
          ...cardHeaderSx(accentColor),
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <CardTitle sx={cardTitleSx(accentColor)}>
          <Storefront fontSize="small" sx={{ mr: 1 }} />
          En Stock
        </CardTitle>
        {!loading && total > 0 && (
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {pieceCountLabel(total)}
          </Typography>
        )}
      </CardHeader>
      <CardContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error != null ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        ) : units.length === 0 ? (
          <Box sx={{ px: 2, py: 2 }}>
            <NoTagsText>No hay piezas en sucursal.</NoTagsText>
          </Box>
        ) : (
          <>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {units.map((unit) => {
                const clickable = canViewInventoryGeneral;
                return (
                  <Box
                    component="li"
                    key={unit.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      minHeight: 32,
                      px: 1.5,
                      py: 0.25,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-of-type': { borderBottom: 0 },
                    }}
                  >
                    {clickable ? (
                      <Link
                        component="button"
                        type="button"
                        underline="always"
                        aria-label={`Ver ${unit.productCodeUnique} en inventario general`}
                        onClick={() => handleOpenInGeneralInventory(unit.productCodeUnique)}
                        sx={{
                          flex: '0 1 7.5rem',
                          fontWeight: 700,
                          fontSize: '0.8125rem',
                          lineHeight: 1.4,
                          textAlign: 'left',
                          color: TABLE_ACTION_ICON_COLOR,
                          textUnderlineOffset: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          '&:hover': { color: TABLE_ACTION_ICON_COLOR },
                        }}
                      >
                        {unit.productCodeUnique}
                      </Link>
                    ) : (
                      <Typography
                        fontWeight={700}
                        fontSize="0.8125rem"
                        noWrap
                        sx={{ flex: '0 1 7.5rem' }}
                      >
                        {unit.productCodeUnique}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ flex: 1, minWidth: 0, fontSize: '0.75rem' }}
                    >
                      {unit.locationName}
                    </Typography>
                    <StatusChip
                      label={availabilityLabel(unit.availabilityStatus)}
                      status={availabilityChipStatus(unit.availabilityStatus)}
                      size="small"
                      density="compact"
                      visualStyle="quiet"
                    />
                  </Box>
                );
              })}
            </Box>
            {showPagination && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.75 }}>
                <Pagination
                  size="small"
                  page={page}
                  count={totalPages}
                  onChange={(_, nextPage) => setPage(nextPage)}
                />
              </Box>
            )}
          </>
        )}
      </CardContent>
    </InfoCard>
  );
};

export default ProductCatalogStockSection;
